import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { printPdfBlob } from "@/lib/printPdf";
import { downloadCompactOnboardingPdf } from "@/lib/compactPdf";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";
import { IntakeField } from "@/components/IntakeField";
import SoapSessionDialog, { SoapNotesList } from "@/components/SoapSessionDialog";
import { ClipboardList, Copy, Eye, ExternalLink, FileText, Download, Printer, Send, Mic } from "lucide-react";

function StatusBadge({ status }) {
  const tone =
    status === "submitted" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : status === "expired" ? "bg-zinc-100 text-zinc-600 border-zinc-200"
    : "bg-amber-100 text-amber-800 border-amber-200";
  return (
    <Badge className={`uppercase text-2xs tracking-wider border ${tone}`}>
      {status}
    </Badge>
  );
}

export default function IntakeForms() {
  const { can } = useAuth();
  const editable = can("owner", "manager");
  const [tab, setTab] = useState("submissions");
  // Hoisted state so both tabs can request opens of the standalone send
  // dialog. Radix's TabsContent unmounts inactive tabs, so any state that
  // needs to survive tab switches — or be triggered from a NON-active tab
  // — must live here on the always-mounted parent.
  const [standaloneOpen, setStandaloneOpen] = useState(false);
  const [standalonePrefill, setStandalonePrefill] = useState(null);
  const [refreshSubmissionsKey, setRefreshSubmissionsKey] = useState(0);

  function requestStandaloneSend(prefill) {
    setStandalonePrefill(prefill || null);
    setStandaloneOpen(true);
  }

  return (
    <div className="space-y-6" data-testid="intake-forms-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          Client onboarding
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 flex items-center gap-3">
          <ClipboardList className="h-8 w-8" /> Client Onboarding — forms &amp; questionnaires
        </h1>
        <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
          Send a registration &amp; consent form to clients when they book in. Track submissions
          and browse client history.{" "}
          <Link
            to="/settings?tab=onboarding"
            className="text-zinc-950 underline underline-offset-2 font-medium hover:text-zinc-700"
            data-testid="manage-templates-link"
          >
            Manage templates in Settings →
          </Link>
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList data-testid="intake-tabs" className="bg-zinc-100">
          <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">Client history</TabsTrigger>
        </TabsList>

        <TabsContent value="submissions" className="mt-0">
          <SubmissionsTab
            editable={editable}
            onRequestStandaloneSend={() => requestStandaloneSend(null)}
            refreshKey={refreshSubmissionsKey}
          />
        </TabsContent>
        <TabsContent value="clients" className="mt-0">
          <ClientHistoryTab
            editable={editable}
            onRequestStandaloneSend={(prefill) => {
              // Flip to the Submissions tab so the operator can watch the
              // new submission land, then open the dialog with the client
              // pre-populated.
              setTab("submissions");
              requestStandaloneSend(prefill);
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Always-mounted so any tab can trigger it */}
      <StandaloneSendDialog
        open={standaloneOpen}
        prefill={standalonePrefill}
        onClose={() => setStandaloneOpen(false)}
        onSent={() => {
          setStandaloneOpen(false);
          setRefreshSubmissionsKey((k) => k + 1);
        }}
      />
    </div>
  );
}

// ============================================================================
// Submissions tab
// ============================================================================

function SubmissionsTab({ editable, onRequestStandaloneSend, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  // Debounced copy so filter recomputation doesn't happen on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(h);
  }, [search]);
  const [viewing, setViewing] = useState(null);

  // Allow deep-linking to a specific submission via ?view=<token>.
  // Handy when the operator wants to share a link to a completed form (or
  // when a preview submission needs to be inspected — those don't appear
  // in the /intake/submissions list because they're preview=true).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("view");
    if (t) setViewing(t);
  }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/intake/submissions");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load submissions", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);
  // Parent bumps `refreshKey` after a standalone send completes so the new
  // submission lands here without the operator having to click Refresh.
  useEffect(() => {
    if (refreshKey > 0) load();
  }, [refreshKey]);

  async function resend(row) {
    const alt = window.prompt(
      `Resend the ${row.template_kind === "questionnaire" ? "questionnaire" : "form"} link to which email?\n\n` +
      `Leave blank to use the original recipient (${row.client_email || "no email on file"}).`,
      "",
    );
    if (alt === null) return;   // user cancelled
    const payload = alt.trim() ? { to_email: alt.trim() } : {};
    try {
      const r = await api.post(`/intake/submissions/${row.token}/resend`, payload);
      if (r.data.ok) {
        toast.success(
          r.data.extended ? "Link extended and resent" : "Resent",
          { description: `Sent to ${r.data.to}` },
        );
        load();
      } else {
        toast.error("Send failed");
      }
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function extendOne(row) {
    try {
      await api.post(`/intake/submissions/${row.token}/extend`);
      toast.success("Link extended", {
        description: "This link is active again — you can now resend it.",
      });
      load();
    } catch (e) {
      toast.error("Extend failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  const filtered = useMemo(() => {
    let list = filter === "all" ? rows : rows.filter((r) => r.status === filter);
    if (debouncedSearch) {
      list = list.filter((r) =>
        (r.client_name || "").toLowerCase().includes(debouncedSearch)
        || (r.client_email || "").toLowerCase().includes(debouncedSearch),
      );
    }
    return list;
  }, [rows, filter, debouncedSearch]);

  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          {filtered.length} {filtered.length === 1 ? "submission" : "submissions"}
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email…"
            className="h-8 w-[220px] bg-white"
            data-testid="intake-search"
          />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-[160px] bg-white" data-testid="intake-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load}>Refresh</Button>
          {editable && (
            <Button
              size="sm"
              onClick={() => onRequestStandaloneSend?.()}
              data-testid="send-standalone-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> Send questionnaire
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="intake-submissions-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Client</th>
              <th className="py-2 pr-4">Appointment</th>
              <th className="py-2 pr-4">Template</th>
              <th className="py-2 pr-4">Sent</th>
              <th className="py-2 pr-4">Submitted</th>
              <th className="py-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-4 text-zinc-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-zinc-500 text-center">No submissions yet.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.token} className="border-b border-zinc-100 align-top" data-testid={`intake-row-${r.token}`}>
                <td className="py-3 pr-4"><StatusBadge status={r.status} /></td>
                <td className="py-3 pr-4">
                  <div className="font-medium text-zinc-950">{r.client_name || "—"}</div>
                  <div className="text-xs text-zinc-500 font-mono-num">{r.client_email}</div>
                </td>
                <td className="py-3 pr-4">
                  <div className="text-zinc-900">{r.appointment_type_name || "—"}</div>
                  <div className="text-xs text-zinc-500">
                    {r.appointment_datetime_iso ? fmtDateTime(r.appointment_datetime_iso) : "—"}
                  </div>
                </td>
                <td className="py-3 pr-4 text-zinc-700">{r.template_name || "—"}</td>
                <td className="py-3 pr-4 text-zinc-600">{r.sent_at ? fmtDateTime(r.sent_at) : "—"}</td>
                <td className="py-3 pr-4 text-zinc-600">{r.submitted_at ? fmtDateTime(r.submitted_at) : "—"}</td>
                <td className="py-3 pr-4">
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`intake-view-${r.token}`}
                      onClick={() => setViewing(r.token)}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                    {r.status !== "submitted" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`intake-resend-${r.token}`}
                        onClick={() => resend(r)}
                      >
                        <Send className="h-3.5 w-3.5 mr-1" /> Resend
                      </Button>
                    )}
                    {r.status === "expired" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`intake-extend-${r.token}`}
                        onClick={() => extendOne(r)}
                        title="Revive this expired link with a fresh expiry"
                      >
                        Extend
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SubmissionDetailDialog token={viewing} onClose={() => setViewing(null)} />
    </Card>
  );
}

// ============================================================================
// Standalone send dialog — send a questionnaire (or any template) to a
// client without needing an Acuity appointment. Used for repeat assessments.
// ============================================================================

export function StandaloneSendDialog({ open, prefill, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Load ALL templates so operators can pick any — including intake forms
    // if they need to re-collect registration info.
    api.get("/intake/templates").then((r) => {
      setTemplates(r.data || []);
      // Default to the first questionnaire so PDQ-39 is one click away.
      const firstQ = (r.data || []).find((t) => t.kind === "questionnaire");
      setTemplateId((prev) => prev || firstQ?.id || (r.data?.[0]?.id ?? ""));
    }).catch((e) => {
      toast.error("Could not load templates", { description: e.message });
    });
  }, [open]);

  useEffect(() => {
    if (open && prefill) {
      setEmail(prefill.client_email || "");
      setName(prefill.client_name || "");
      setNote(prefill.note || "");
    } else if (!open) {
      // Clear the form when the dialog closes so the next open is fresh.
      setEmail(""); setName(""); setNote("");
    }
  }, [open, prefill]);

  const questionnaires = useMemo(
    () => templates.filter((t) => t.kind === "questionnaire"),
    [templates],
  );
  const intakes = useMemo(
    () => templates.filter((t) => t.kind !== "questionnaire"),
    [templates],
  );

  async function submit() {
    if (!email.trim() || !templateId) {
      toast.error("Client email and template are required");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/intake/questionnaires/send-standalone", {
        template_id: templateId,
        client_email: email.trim(),
        client_name: name.trim(),
        note: note.trim(),
      });
      toast.success("Questionnaire queued", {
        description: `Sent to ${r.data.to}. Link: ${r.data.link}`,
      });
      onSent?.();
    } catch (e) {
      toast.error("Send failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="standalone-send-dialog">
        <DialogHeader>
          <DialogTitle>Send questionnaire</DialogTitle>
          <DialogDescription>
            Send a fresh copy of any template to a client — great for repeat PDQ-39 assessments at 3, 6, and 12 months.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div>
            <Label className="text-xs">Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="standalone-template" className="mt-1">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {questionnaires.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-2xs uppercase tracking-wider text-zinc-400">
                      Questionnaires
                    </div>
                    {questionnaires.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </>
                )}
                {intakes.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-2xs uppercase tracking-wider text-zinc-400">
                      Intake forms
                    </div>
                    {intakes.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Client email *</Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                type="email"
                className="mt-1"
                data-testid="standalone-email"
              />
            </div>
            <div>
              <Label className="text-xs">Client name (optional)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1"
                data-testid="standalone-name"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">
              Note (optional — shown at the top of the email)
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. 3-month reassessment — please complete before your next visit."
              className="mt-1 min-h-[64px] text-sm"
              maxLength={300}
              data-testid="standalone-note"
            />
            <p className="mt-1 text-2xs text-zinc-500">
              {note.length}/300 characters. Great for repeat PDQ-39 assessments — the client will see this note explaining why they&apos;re receiving the form again.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={sending} data-testid="standalone-submit">
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Sending…" : "Send now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Client history tab — one row per client_email, drill in for full timeline
// + PDQ-39 trend chart.
// ============================================================================

export function ClientHistoryTab({ editable, onRequestStandaloneSend }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeEmail, setActiveEmail] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/intake/clients", { params: { q: q || undefined } });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load clients", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);
  // Debounced search — refetch 300ms after the operator stops typing.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  function openStandaloneSendFor(row) {
    // Delegate to the parent — it flips the tab AND opens the dialog with
    // this client pre-populated. Radix TabsContent unmounts inactive tabs,
    // so cross-tab plumbing has to go through always-mounted state.
    onRequestStandaloneSend?.({
      client_email: row.client_email,
      client_name: row.client_name,
      note: "Follow-up assessment",
    });
  }

  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none" data-testid="client-history-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          {rows.length} {rows.length === 1 ? "client" : "clients"} with submissions
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or name…"
            className="h-8 w-full sm:w-64 bg-white"
            data-testid="client-search"
          />
          <Button variant="outline" size="sm" onClick={load}>Refresh</Button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="client-history-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <th className="py-2 pr-4">Client</th>
              <th className="py-2 pr-4 text-right">Submissions</th>
              <th className="py-2 pr-4 text-right">Completed</th>
              <th className="py-2 pr-4">Latest PDQ-SI</th>
              <th className="py-2 pr-4">Latest ONERO</th>
              <th className="py-2 pr-4">Last activity</th>
              <th className="py-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-4 text-zinc-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-zinc-500">
                  {q ? `No matches for "${q}".` : "No clients yet."}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.email_key}
                className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                onClick={() => setActiveEmail(r.client_email)}
                data-testid={`client-row-${r.email_key}`}
              >
                <td className="py-3 pr-4">
                  <div className="font-medium text-zinc-950">{r.client_name || "—"}</div>
                  <div className="text-xs text-zinc-500 font-mono-num">{r.client_email}</div>
                </td>
                <td className="py-3 pr-4 text-right font-mono-num text-zinc-700">
                  {r.total_submissions}
                </td>
                <td className="py-3 pr-4 text-right font-mono-num text-zinc-700">
                  {r.submitted_count}
                </td>
                <td className="py-3 pr-4">
                  {r.latest_pdq39_summary != null ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 px-2 py-0.5 text-xs font-mono-num">
                      {r.latest_pdq39_summary.toFixed(1)} / 100
                    </span>
                  ) : (
                    <span className="text-xs text-zinc-400">—</span>
                  )}
                </td>
                <td className="py-3 pr-4">
                  {r.latest_onero_composite != null ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-sky-50 border border-sky-200 text-sky-900 px-2 py-0.5 text-xs font-mono-num" data-testid={`onero-composite-${r.email_key}`}>
                      {r.latest_onero_composite.toFixed(1)} / 100
                    </span>
                  ) : (
                    <span className="text-xs text-zinc-400">—</span>
                  )}
                </td>
                <td className="py-3 pr-4 text-xs text-zinc-600">
                  {r.last_activity_at ? fmtDateTime(r.last_activity_at) : "—"}
                </td>
                <td className="py-3 pr-4">
                  <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActiveEmail(r.client_email)}
                      data-testid={`client-view-${r.email_key}`}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" /> History
                    </Button>
                    {editable && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openStandaloneSendFor(r)}
                        data-testid={`client-send-${r.email_key}`}
                      >
                        <Send className="h-3.5 w-3.5 mr-1" /> Send follow-up
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ClientHistoryDialog
        email={activeEmail}
        clientName={rows.find((r) => r.client_email === activeEmail)?.client_name || ""}
        editable={editable}
        onClose={() => setActiveEmail(null)}
      />
    </Card>
  );
}

function ClientHistoryDialog({ email, clientName, editable, onClose }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [soapOpen, setSoapOpen] = useState(false);
  const [soapRefresh, setSoapRefresh] = useState(0);

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    api.get(`/intake/clients/by-email/${encodeURIComponent(email)}/submissions`)
      .then((r) => setSubs(r.data || []))
      .catch((e) => {
        toast.error("Could not load history", { description: e?.response?.data?.detail || e.message });
      })
      .finally(() => setLoading(false));
  }, [email]);

  // Extract PDQ-39 submissions in chronological order for the trend chart.
  const pdqTrend = useMemo(() => {
    return subs
      .filter((s) => s.auto_score?.kind === "pdq39" && s.auto_score?.summary_index != null)
      .map((s) => ({
        submitted_at: s.submitted_at || s.created_at,
        si: s.auto_score.summary_index,
        subscales: s.auto_score.subscales,
        labels: s.auto_score.labels,
        order: s.auto_score.order,
        token: s.token,
      }))
      .reverse();
  }, [subs]);

  // Extract ONERO baseline submissions in chronological order — mirrors
  // the PDQ trend but with 3 measures + a "higher = better" composite so
  // the chart legend swaps direction cues automatically.
  const oneroTrend = useMemo(() => {
    return subs
      .filter((s) => s.auto_score?.kind === "onero" && s.auto_score?.composite != null)
      .map((s) => ({
        submitted_at: s.submitted_at || s.created_at,
        composite: s.auto_score.composite,
        measures: s.auto_score.measures,
        labels: s.auto_score.labels,
        order: s.auto_score.order,
        token: s.token,
      }))
      .reverse();
  }, [subs]);

  return (
    <Dialog open={!!email} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono-num text-base">
            {email}
          </DialogTitle>
          <DialogDescription>
            Complete submission history for this client, with a PDQ-39 or ONERO® trend chart when 2+ assessments exist.
          </DialogDescription>
        </DialogHeader>
        {editable && (
          <div className="flex justify-end -mt-2">
            <Button
              size="sm"
              onClick={() => setSoapOpen(true)}
              data-testid="soap-record-btn"
            >
              <Mic className="h-3.5 w-3.5 mr-1" /> Record consultation
            </Button>
          </div>
        )}
        {loading ? (
          <div className="py-6 text-zinc-500 text-sm">Loading history…</div>
        ) : (
          <div className="space-y-5 pt-1">
            {pdqTrend.length >= 1 && (
              <PDQTrendChart data={pdqTrend} />
            )}
            {oneroTrend.length >= 1 && (
              <OneroTrendChart data={oneroTrend} />
            )}
            <SoapNotesList email={email} refreshKey={soapRefresh} />
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">
                Timeline ({subs.length} submission{subs.length === 1 ? "" : "s"})
              </h4>
              {subs.length === 0 ? (
                <div className="text-sm text-zinc-500 py-4">No submissions.</div>
              ) : (
                <ul className="space-y-2">
                  {subs.map((s) => (
                    <li
                      key={s.token}
                      className="rounded-lg border border-zinc-200 px-4 py-3 flex items-start justify-between gap-3 hover:bg-zinc-50"
                      data-testid={`history-item-${s.token}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusBadge status={s.status} />
                          <span className="text-sm font-medium text-zinc-900 truncate">
                            {s.template_name || "—"}
                          </span>
                          {s.standalone_send && (
                            <Badge variant="outline" className="text-2xs bg-blue-50 border-blue-200 text-blue-900">
                              Follow-up
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-zinc-600 space-x-2">
                          <span>Sent {s.sent_at ? fmtDateTime(s.sent_at) : "—"}</span>
                          {s.submitted_at && (
                            <span>· Submitted {fmtDateTime(s.submitted_at)}</span>
                          )}
                          {s.appointment_type_name && (
                            <span>· {s.appointment_type_name}</span>
                          )}
                        </div>
                        {s.standalone_note && (
                          <div className="mt-1 text-xs text-zinc-500 italic">
                            &ldquo;{s.standalone_note}&rdquo;
                          </div>
                        )}
                        {s.auto_score?.kind === "pdq39" && s.auto_score?.summary_index != null && (
                          <div className="mt-1 text-xs">
                            <span className="font-medium text-emerald-900">PDQ-SI:</span>{" "}
                            <span className="font-mono-num">{s.auto_score.summary_index.toFixed(1)} / 100</span>
                          </div>
                        )}
                        {s.auto_score?.kind === "onero" && s.auto_score?.composite != null && (
                          <div className="mt-1 text-xs">
                            <span className="font-medium text-sky-900">ONERO composite:</span>{" "}
                            <span className="font-mono-num">{s.auto_score.composite.toFixed(1)} / 100</span>
                            <span className="ml-2 text-zinc-500">
                              (higher = better)
                            </span>
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // Deep-link into the Submissions tab dialog
                          const url = new URL(window.location.href);
                          url.searchParams.set("view", s.token);
                          window.history.pushState({}, "", url);
                          // Flip to Submissions tab and trigger the effect
                          const trigger = document.querySelector("[data-testid='tab-submissions']");
                          if (trigger) trigger.click();
                          onClose?.();
                          // Nudge the URL-param effect to re-run
                          window.dispatchEvent(new PopStateEvent("popstate"));
                        }}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </DialogContent>
      {soapOpen && (
        <SoapSessionDialog
          open={soapOpen}
          onClose={() => { setSoapOpen(false); setSoapRefresh((n) => n + 1); }}
          clientEmail={email}
          clientName={clientName}
          onSaved={() => setSoapRefresh((n) => n + 1)}
        />
      )}
    </Dialog>
  );
}

function PDQTrendChart({ data }) {
  // Simple inline SVG line chart — no chart library dependency needed for
  // 8 subscales × N data points. Y axis 0-100, x axis time.
  const width = 640;
  const height = 240;
  const pad = { top: 16, right: 24, bottom: 32, left: 40 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;

  if (data.length === 0) return null;
  if (data.length === 1) {
    // With a single data point a line chart is meaningless — show a stacked
    // summary of the subscales as a horizontal bar breakdown instead.
    const only = data[0];
    return (
      <section
        className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4"
        data-testid="pdq-trend-single"
      >
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-900">
            PDQ-39 baseline
          </h4>
          <div className="text-xs text-emerald-800/70">
            PDQ-SI:{" "}
            <span className="text-base font-semibold font-mono-num text-emerald-900">
              {only.si.toFixed(1)}
            </span>
            <span className="opacity-60"> / 100</span>
          </div>
        </div>
        <p className="mt-1 text-2xs text-emerald-900/60">
          Send a follow-up assessment (e.g. in 3 months) to see progression here.
        </p>
      </section>
    );
  }

  const times = data.map((d) => new Date(d.submitted_at).getTime());
  const tMin = Math.min(...times);
  const tMax = Math.max(...times);
  const tRange = Math.max(1, tMax - tMin);
  const xFor = (t) => pad.left + ((new Date(t).getTime() - tMin) / tRange) * chartW;
  const yFor = (v) => pad.top + (1 - v / 100) * chartH;

  // Summary Index line — the emphasis line
  const siPath = data.map((d, i) => `${i === 0 ? "M" : "L"}${xFor(d.submitted_at).toFixed(1)},${yFor(d.si).toFixed(1)}`).join(" ");

  const order = data[0].order || [];
  const labels = data[0].labels || {};

  // Muted subscale lines behind the SI line
  const subColors = {
    mobility: "#0891b2",
    adl: "#059669",
    emotional: "#d97706",
    stigma: "#7c3aed",
    social: "#db2777",
    cognition: "#2563eb",
    communication: "#65a30d",
    bodily: "#dc2626",
  };
  const subscalePaths = order.map((k) => {
    const pathD = data
      .map((d, i) => {
        const v = d.subscales?.[k]?.score;
        if (v == null) return null;
        return `${i === 0 ? "M" : "L"}${xFor(d.submitted_at).toFixed(1)},${yFor(v).toFixed(1)}`;
      })
      .filter(Boolean)
      .join(" ");
    return { key: k, label: labels[k] || k, color: subColors[k] || "#71717a", d: pathD };
  });

  const yTicks = [0, 25, 50, 75, 100];
  const dateFmt = (t) => new Date(t).toLocaleDateString();

  return (
    <section
      className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4"
      data-testid="pdq-trend-chart"
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-900">
          PDQ-39 trend ({data.length} assessments)
        </h4>
        <div className="text-xs text-emerald-800/70">
          Higher = worse
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="PDQ-39 trend chart">
        {yTicks.map((y) => (
          <g key={y}>
            <line
              x1={pad.left} x2={width - pad.right}
              y1={yFor(y)} y2={yFor(y)}
              stroke="#d1fae5" strokeDasharray={y === 0 ? "0" : "3 3"}
            />
            <text x={pad.left - 6} y={yFor(y) + 3} textAnchor="end" fontSize="10" fill="#065f46">{y}</text>
          </g>
        ))}
        {/* X-axis date labels for first + last point */}
        <text x={xFor(data[0].submitted_at)} y={height - 8} textAnchor="start" fontSize="10" fill="#065f46">
          {dateFmt(data[0].submitted_at)}
        </text>
        <text x={xFor(data[data.length - 1].submitted_at)} y={height - 8} textAnchor="end" fontSize="10" fill="#065f46">
          {dateFmt(data[data.length - 1].submitted_at)}
        </text>

        {/* Muted subscale lines */}
        {subscalePaths.map((s) => (
          <path key={s.key} d={s.d} stroke={s.color} strokeWidth="1" fill="none" opacity="0.35" />
        ))}
        {/* Prominent SI line */}
        <path d={siPath} stroke="#065f46" strokeWidth="2.5" fill="none" />
        {data.map((d, i) => (
          <g key={i}>
            <circle cx={xFor(d.submitted_at)} cy={yFor(d.si)} r="4" fill="#065f46" />
            <text
              x={xFor(d.submitted_at)}
              y={yFor(d.si) - 8}
              textAnchor="middle" fontSize="10" fill="#065f46" fontWeight="600"
            >
              {d.si.toFixed(1)}
            </text>
          </g>
        ))}
      </svg>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-2xs text-emerald-900/70">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-emerald-900" /> PDQ-SI (bold)
        </span>
        {subscalePaths.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-0.5" style={{ background: s.color, opacity: 0.6 }} />
            {s.label}
          </span>
        ))}
      </div>
    </section>
  );
}

function PDQ39ScoreCard({ score }) {
  // Auto-computed PDQ-39 subscale scores + Summary Index.
  // Higher = worse HRQoL impact. Range 0–100 per subscale.
  const order = score?.order || [];
  const labels = score?.labels || {};
  const subs = score?.subscales || {};
  const si = score?.summary_index;
  return (
    <section
      className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-3"
      data-testid="pdq39-score-card"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-emerald-900">
          PDQ-39 auto-scored results
        </h3>
        <div className="text-xs text-emerald-800/70 font-mono-num">
          Summary Index (PDQ-SI):{" "}
          <span
            className="text-lg font-semibold text-emerald-900 ml-1"
            data-testid="pdq39-summary-index"
          >
            {si == null ? "—" : si.toFixed(1)}
          </span>
          <span className="opacity-70"> / 100</span>
        </div>
      </div>
      <p className="text-2xs text-emerald-900/70 leading-relaxed -mt-1">
        Higher scores indicate greater impact of Parkinson&apos;s disease on that
        dimension. Range 0 (no impact) → 100 (worst impact).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {order.map((k) => {
          const s = subs[k] || {};
          const insufficient = s.insufficient || s.score == null;
          return (
            <div
              key={k}
              className="flex items-baseline justify-between gap-3 rounded-md bg-white border border-emerald-100 px-3 py-2"
              data-testid={`pdq39-subscale-${k}`}
            >
              <div className="text-xs font-medium text-emerald-950">
                {labels[k] || k}
                <div className="text-2xs text-emerald-800/60 font-mono-num">
                  {s.answered ?? 0}/{s.total_items ?? 0} answered
                </div>
              </div>
              <div className="text-right">
                {insufficient ? (
                  <em className="text-2xs text-emerald-800/60">
                    Insufficient data
                  </em>
                ) : (
                  <>
                    <div className="text-lg font-semibold text-emerald-900 font-mono-num">
                      {s.score.toFixed(1)}
                    </div>
                    <div className="text-2xs text-emerald-800/60 font-mono-num">
                      raw {s.raw}/{s.max}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {score.missing?.length ? (
        <p className="text-2xs text-emerald-800/70">
          Unanswered items: Q{score.missing.join(", Q")}.
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// ONERO® trend chart — mirrors PDQTrendChart but plots the 3 baseline
// measures + a "higher = better" composite (bone-health function). The
// composite is the emphasis line; individual measures are muted behind.
// ---------------------------------------------------------------------------
function OneroTrendChart({ data }) {
  const width = 640;
  const height = 240;
  const pad = { top: 16, right: 24, bottom: 32, left: 40 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;

  if (data.length === 0) return null;
  if (data.length === 1) {
    const only = data[0];
    return (
      <section
        className="rounded-lg border border-sky-200 bg-sky-50/40 p-4"
        data-testid="onero-trend-single"
      >
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-sky-900">
            ONERO® baseline
          </h4>
          <div className="text-xs text-sky-800/70">
            Composite:{" "}
            <span className="text-base font-semibold font-mono-num text-sky-900">
              {only.composite.toFixed(1)}
            </span>
            <span className="opacity-60"> / 100</span>
          </div>
        </div>
        <p className="mt-1 text-2xs text-sky-900/60">
          Send a follow-up baseline (e.g. in 3 months) to see progression here.
        </p>
      </section>
    );
  }

  const times = data.map((d) => new Date(d.submitted_at).getTime());
  const tMin = Math.min(...times);
  const tMax = Math.max(...times);
  const tRange = Math.max(1, tMax - tMin);
  const xFor = (t) => pad.left + ((new Date(t).getTime() - tMin) / tRange) * chartW;
  const yFor = (v) => pad.top + (1 - v / 100) * chartH;

  const compositePath = data
    .map((d, i) => `${i === 0 ? "M" : "L"}${xFor(d.submitted_at).toFixed(1)},${yFor(d.composite).toFixed(1)}`)
    .join(" ");

  const order = data[0].order || [];
  const labels = data[0].labels || {};

  // Colours picked to feel distinct from the PDQ palette (emerald) —
  // ONERO gets sky/indigo hues.
  const measureColors = {
    physical_function: "#0369a1",
    confidence: "#7c3aed",
    falls_concern: "#f97316",
  };

  const measurePaths = order.map((k) => {
    const pathD = data
      .map((d, i) => {
        const v = d.measures?.[k]?.normalised;
        if (v == null) return null;
        return `${i === 0 ? "M" : "L"}${xFor(d.submitted_at).toFixed(1)},${yFor(v).toFixed(1)}`;
      })
      .filter(Boolean)
      .join(" ");
    return { key: k, label: labels[k] || k, color: measureColors[k] || "#71717a", d: pathD };
  });

  const yTicks = [0, 25, 50, 75, 100];
  const dateFmt = (t) => new Date(t).toLocaleDateString();

  return (
    <section
      className="rounded-lg border border-sky-200 bg-sky-50/40 p-4"
      data-testid="onero-trend-chart"
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-sky-900">
          ONERO® trend ({data.length} assessments)
        </h4>
        <div className="text-xs text-sky-800/70">Higher = better function</div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="ONERO trend chart">
        {yTicks.map((y) => (
          <g key={y}>
            <line
              x1={pad.left} x2={width - pad.right}
              y1={yFor(y)} y2={yFor(y)}
              stroke="#e0f2fe" strokeDasharray={y === 0 ? "0" : "3 3"}
            />
            <text x={pad.left - 6} y={yFor(y) + 3} textAnchor="end" fontSize="10" fill="#0c4a6e">{y}</text>
          </g>
        ))}
        <text x={xFor(data[0].submitted_at)} y={height - 8} textAnchor="start" fontSize="10" fill="#0c4a6e">
          {dateFmt(data[0].submitted_at)}
        </text>
        <text x={xFor(data[data.length - 1].submitted_at)} y={height - 8} textAnchor="end" fontSize="10" fill="#0c4a6e">
          {dateFmt(data[data.length - 1].submitted_at)}
        </text>
        {measurePaths.map((m) => (
          <path key={m.key} d={m.d} stroke={m.color} strokeWidth="1" fill="none" opacity="0.4" />
        ))}
        <path d={compositePath} stroke="#0c4a6e" strokeWidth="2.5" fill="none" />
        {data.map((d, i) => (
          <g key={i}>
            <circle cx={xFor(d.submitted_at)} cy={yFor(d.composite)} r="4" fill="#0c4a6e" />
            <text
              x={xFor(d.submitted_at)}
              y={yFor(d.composite) - 8}
              textAnchor="middle" fontSize="10" fill="#0c4a6e" fontWeight="600"
            >
              {d.composite.toFixed(1)}
            </text>
          </g>
        ))}
      </svg>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-2xs text-sky-900/70">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-sky-900" /> Composite (bold)
        </span>
        {measurePaths.map((m) => (
          <span key={m.key} className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-0.5" style={{ background: m.color, opacity: 0.6 }} />
            {m.label}
          </span>
        ))}
      </div>
    </section>
  );
}

function OneroBaselineScoreCard({ score, previousDexa = null }) {
  // Auto-computed ONERO baseline measures + composite. Higher = BETTER
  // (opposite of PDQ-SI) so the visual palette flips to sky/indigo to
  // hint at the different direction.
  const order = score?.order || [];
  const labels = score?.labels || {};
  const measures = score?.measures || {};
  const composite = score?.composite;
  const dexa = score?.dexa || null;
  // WHO band → Tailwind tone map. Kept explicit rather than derived
  // from `dexa.band_tone` so a stray backend value can't break the
  // build (Tailwind purges unknown class names).
  const DEXA_TONE = {
    osteoporosis: "bg-red-50 border-red-300 text-red-900",
    osteopenia: "bg-amber-50 border-amber-300 text-amber-900",
    normal: "bg-emerald-50 border-emerald-300 text-emerald-900",
  };
  // Compute delta vs prior reading when both are available. A worse
  // T-Score (more negative) is a decline — surface it with a red down-
  // arrow; an improvement uses green up-arrow.
  let deltaBlock = null;
  if (dexa?.worst != null && previousDexa?.worst != null) {
    const delta = +(dexa.worst - previousDexa.worst).toFixed(2);
    const bandChanged = dexa.band !== previousDexa.band;
    const improved = delta > 0;
    // Arrow points to direction of change on the T-Score axis.
    const arrow = delta === 0 ? "→" : improved ? "▲" : "▼";
    const arrowColor = delta === 0
      ? "text-zinc-500"
      : improved
        ? "text-emerald-700"
        : "text-red-700";
    const priorDate = previousDexa.at ? String(previousDexa.at).slice(0, 10) : "";
    deltaBlock = (
      <div className="flex items-center gap-1.5 text-2xs mt-1 pt-1 border-t border-dashed border-current/30"
           data-testid="onero-dexa-trend">
        <span className="opacity-70">vs prior</span>
        <span className={`font-semibold font-mono-num ${arrowColor}`}
              data-testid="onero-dexa-delta">
          {arrow} {delta > 0 ? "+" : ""}{delta.toFixed(1)}
        </span>
        <span className="opacity-60 font-mono-num">
          (was {previousDexa.worst.toFixed(1)}
          {priorDate && `, ${priorDate}`})
        </span>
        {bandChanged && (
          <span className="ml-auto px-1.5 py-0.5 rounded bg-white/60 border border-current/30 text-2xs uppercase tracking-wide font-semibold"
                data-testid="onero-dexa-band-change">
            band → {dexa.band_label}
          </span>
        )}
      </div>
    );
  }
  return (
    <section
      className="rounded-xl border border-sky-200 bg-sky-50/60 p-4 space-y-3"
      data-testid="onero-score-card"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-sky-900">
          ONERO® baseline outcome measures
        </h3>
        <div className="text-xs text-sky-800/70 font-mono-num">
          Composite:{" "}
          <span
            className="text-lg font-semibold text-sky-900 ml-1"
            data-testid="onero-composite"
          >
            {composite == null ? "—" : composite.toFixed(1)}
          </span>
          <span className="opacity-70"> / 100</span>
        </div>
      </div>
      {dexa?.band && (
        <div
          className={`rounded-md border px-3 py-2 text-xs ${DEXA_TONE[dexa.band] || "bg-white border-slate-200 text-slate-900"}`}
          data-testid="onero-fracture-risk-band"
          title="Auto-parsed from the client's free-text DEXA scores using WHO T-Score bands"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-2xs uppercase tracking-wide font-semibold opacity-80">
                Fracture risk
              </span>
              <span className="font-semibold" data-testid="onero-fracture-risk-label">
                {dexa.band_label}
              </span>
            </div>
            <div className="font-mono-num">
              worst T-Score{" "}
              <span data-testid="onero-fracture-risk-worst">
                {dexa.worst?.toFixed(1)}
              </span>
              {dexa.values?.length > 1 && (
                <span className="opacity-70"> · {dexa.values.length} readings</span>
              )}
            </div>
          </div>
          {deltaBlock}
        </div>
      )}
      <p className="text-2xs text-sky-900/70 leading-relaxed -mt-1">
        Higher scores indicate better self-reported bone-health function.
        Falls concern is inverted (a low raw score = high normalised score)
        so all three measures contribute in the same direction.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {order.map((k) => {
          const m = measures[k] || {};
          const missing = m.normalised == null;
          return (
            <div
              key={k}
              className="flex items-baseline justify-between gap-3 rounded-md bg-white border border-sky-100 px-3 py-2"
              data-testid={`onero-measure-${k}`}
            >
              <div className="text-xs font-medium text-sky-950">
                {labels[k] || k}
                <div className="text-2xs text-sky-800/60 font-mono-num">
                  raw {m.raw ?? "—"}/10
                </div>
              </div>
              <div className="text-right">
                {missing ? (
                  <em className="text-2xs text-sky-800/60">Not answered</em>
                ) : (
                  <>
                    <div className="text-lg font-semibold text-sky-900 font-mono-num">
                      {m.normalised.toFixed(1)}
                    </div>
                    <div className="text-2xs text-sky-800/60">/ 100</div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SubmissionDetailDialog({ token, onClose }) {
  const [data, setData] = useState(null);
  // Prior DEXA reading pulled from the client's other Onero submissions
  // so `OneroBaselineScoreCard` can render a "vs previous" delta chip.
  // Null when no earlier reading exists — the card then hides the delta.
  const [previousDexa, setPreviousDexa] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCompact, setDownloadingCompact] = useState(false);
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    if (!token) { setData(null); setPreviousDexa(null); return; }
    api.get(`/intake/submissions/${token}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [token]);
  // Fetch the client's Onero history to find the prior DEXA reading.
  // Runs only when we're actually looking at an Onero submission.
  useEffect(() => {
    if (!data?.client_email || data?.auto_score?.kind !== "onero") {
      setPreviousDexa(null);
      return;
    }
    const currentSubmittedAt = data.submitted_at || data.created_at;
    const email = encodeURIComponent(data.client_email);
    api.get(`/intake/clients/by-email/${email}/submissions`)
      .then((r) => {
        const subs = r.data?.submissions || r.data || [];
        // Find the newest Onero submission with a DEXA band that ISN'T
        // this one and is strictly older than the current submission.
        const priors = subs
          .filter((s) =>
            s.token !== data.token
            && s.auto_score?.kind === "onero"
            && s.auto_score?.dexa?.band
            && s.auto_score?.dexa?.worst != null
            && (!currentSubmittedAt
                || (s.submitted_at || s.created_at) < currentSubmittedAt))
          .sort((a, b) =>
            (b.submitted_at || b.created_at || "").localeCompare(
              a.submitted_at || a.created_at || ""));
        setPreviousDexa(priors[0] ? {
          band: priors[0].auto_score.dexa.band,
          band_label: priors[0].auto_score.dexa.band_label,
          worst: priors[0].auto_score.dexa.worst,
          at: priors[0].submitted_at || priors[0].created_at,
        } : null);
      })
      .catch(() => setPreviousDexa(null));
  }, [data?.token, data?.client_email, data?.auto_score?.kind, data?.submitted_at]);

  async function downloadPdf() {
    if (!data) return;
    setDownloading(true);
    try {
      const res = await api.get(`/intake/submissions/${data.token}/pdf`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `intake_${data.token}.pdf`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e?.message || "PDF download failed";
      const bd = e?.response?.data;
      if (bd instanceof Blob) {
        try { const t = await bd.text(); const j = JSON.parse(t); detail = j.detail || detail; } catch (_) { /* ignore */ }
      }
      toast.error("Could not download PDF", { description: detail });
    } finally { setDownloading(false); }
  }

  async function downloadCompact() {
    if (!data) return;
    setDownloadingCompact(true);
    try {
      await downloadCompactOnboardingPdf(data.token);
    } catch (e) {
      toast.error("Could not download compact PDF", { description: e.message });
    } finally { setDownloadingCompact(false); }
  }

  async function printPdf() {
    if (!data) return;
    setPrinting(true);
    try {
      const res = await api.get(`/intake/submissions/${data.token}/pdf`, { responseType: "blob" });
      await printPdfBlob(new Blob([res.data], { type: "application/pdf" }));
    } catch (e) {
      let detail = e?.message || "Could not print";
      const bd = e?.response?.data;
      if (bd instanceof Blob) {
        try { const t = await bd.text(); const j = JSON.parse(t); detail = j.detail || detail; } catch (_) { /* ignore */ }
      }
      toast.error("Print failed", { description: detail });
    } finally { setPrinting(false); }
  }

  const groups = useMemo(() => {
    const fields = data?.template_snapshot?.fields || [];
    const out = []; const seen = new Map();
    for (const f of fields) {
      const g = f.group || "Details";
      if (!seen.has(g)) { seen.set(g, []); out.push({ name: g, fields: seen.get(g) }); }
      seen.get(g).push(f);
    }
    out.forEach((o) => o.fields = seen.get(o.name));
    return out;
  }, [data]);

  const link = data ? `${window.location.origin}/intake/${data.token}` : "";

  return (
    <Dialog open={!!token} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="intake-detail-dialog">
        <DialogHeader>
          <DialogTitle>{data?.client_name || "Loading…"}</DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <StatusBadge status={data.status} />
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-700">{data.template_name}</span>
              <span className="text-zinc-500">·</span>
              <span className="text-zinc-700">{data.appointment_type_name}</span>
              <span className="text-zinc-500">{data.appointment_datetime_iso ? fmtDateTime(data.appointment_datetime_iso) : ""}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <a href={link} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 underline text-zinc-700 hover:text-zinc-950"
                 data-testid="intake-detail-open-link">
                <ExternalLink className="h-3.5 w-3.5" /> Open form link
              </a>
              <Button
                variant="outline" size="sm" className="h-7"
                onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }}
                data-testid="intake-detail-copy"
              >
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
              </Button>
            </div>
            {data.status === "submitted" ? (
              <div className="space-y-6">
                {data.auto_score?.kind === "pdq39" && (
                  <PDQ39ScoreCard score={data.auto_score} />
                )}
                {data.auto_score?.kind === "onero" && (
                  <OneroBaselineScoreCard score={data.auto_score}
                                           previousDexa={previousDexa} />
                )}
                {groups.map((g) => (
                  <section key={g.name} className="space-y-3">
                    <h3 className="text-sm font-semibold text-zinc-900 border-b border-zinc-200 pb-1">{g.name}</h3>
                    <div className="space-y-3">
                      {g.fields.map((f) => (
                        <IntakeField
                          key={f.id}
                          field={f}
                          value={data.submission?.[f.id]}
                          onChange={() => {}}
                          readOnly
                          downloadEndpoint={
                            f.type === "file" && data.submission?.[f.id]?.storage_path
                              ? `${process.env.REACT_APP_BACKEND_URL}/api/intake/submissions/${data.token}/attachments/${f.id}`
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="text-sm text-zinc-600 px-4 py-6 rounded-md bg-zinc-50 border border-zinc-200">
                No response yet. Sent {data.sent_at ? fmtDateTime(data.sent_at) : "—"};
                {data.reminder_sent_at && <> reminder sent {fmtDateTime(data.reminder_sent_at)};</>}
                expires {data.expires_at ? fmtDateTime(data.expires_at) : "—"}.
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {data && (
            <Button
              variant="outline"
              onClick={printPdf}
              disabled={printing}
              data-testid="intake-detail-print-pdf"
              className="border-zinc-300"
            >
              <Printer className="h-4 w-4 mr-1.5" />
              {printing ? "Preparing…" : "Print"}
            </Button>
          )}
          {data?.status === "submitted" && (
            <Button
              variant="outline"
              onClick={downloadCompact}
              disabled={downloadingCompact}
              data-testid="intake-detail-download-compact-pdf"
              className="border-zinc-300"
            >
              <FileText className="h-4 w-4 mr-1.5" />
              {downloadingCompact ? "Generating…" : "Download compact PDF"}
            </Button>
          )}
          {data && (
            <Button
              onClick={downloadPdf}
              disabled={downloading}
              data-testid="intake-detail-download-pdf"
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {downloading ? "Generating…" : "Download PDF"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

